import TextInput from 'components/Input';
import Slider from 'components/Slider';
import TextAreaInput from 'components/TextArea';
import { device } from 'styles/breakpoints';
import { ActionButton, SectionTextWrapper } from 'styles/global.styles';
import { theme } from 'styles/theme';

import { getSegmentTexts, Segments } from 'data';
import { ChangeEvent } from 'react';
import styled from 'styled-components';

export interface IWorkplace {
  name: string;
  size: number;
  headcount: number;
  apartments: number;
  coLocatedBusinesses: number;
  otherBusinessDescription: string;
}

interface IWorkplaceSection {
  workplace: IWorkplace;
  onClick: () => void;
  selectedSegment: string;
  onChange: (key: keyof IWorkplace, value: string | number) => void;
  validationError?: string;
}

const WorkplaceSection = (props: IWorkplaceSection): JSX.Element => {
  const isCompany = Segments.some(
    (segment) => 'company' in segment && segment.company === props.selectedSegment
  );

  const isWorkplace = Segments.some(
    (segment) => 'worplace' in segment && segment.worplace === props.selectedSegment
  );
  const isResidential = Segments.some(
    (segment) => 'residential' in segment && segment.residential === props.selectedSegment
  );
  const isOther = Segments.some(
    (segment) => 'other' in segment && segment.other === props.selectedSegment
  );

  const copy = getSegmentTexts(props.selectedSegment);

  return (
    <Wrapper>
      <SectionTextWrapper
        sectionNumber={4}
        heading={copy?.workplace.heading ?? ''}
        description={copy?.workplace.description ?? ''}
      />

      {isCompany && (
        <CompanyNameWrapper>
          <TextInput
            name="company_name"
            placeholder="Namn"
            error={props.validationError}
            label={copy?.workplace.companyNameLabel ?? ''}
            value={props.workplace.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange('name', e.target.value)}
          />
        </CompanyNameWrapper>
      )}

      {isWorkplace && (
        <>
          <SliderWrapper
            id="workplace"
            label={copy?.workplace.officeSizeSliderLabel ?? ''}
            step={50}
            value={props.workplace.size}
            onChange={(value: number) => props.onChange('size', value)}
          />

          <EmployeesSliderWrapper
            id="employees"
            label={copy?.workplace.employeesSliderLabel ?? ''}
            step={10}
            max={300}
            value={props.workplace.headcount}
            onChange={(value: number) => props.onChange('headcount', value)}
          />
        </>
      )}

      {isResidential && (
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ResidentialWrapper>
            <InputWrapper>
              <InputText
                type="number"
                min={0}
                step={1}
                name="apartments"
                label={copy?.workplace.apartmentsLabel ?? ''}
                placeholder="0"
                value={props.workplace.apartments || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  props.onChange('apartments', Number(e.target.value))
                }
              />
              <Suffix>st</Suffix>
            </InputWrapper>
          </ResidentialWrapper>
          <InputSpacer />
          <ResidentialWrapper>
            <InputWrapper>
              <InputText
                type="number"
                min={0}
                step={1}
                name="co_located_businesses"
                label={copy?.workplace.coLocatedBusinessesLabel ?? ''}
                placeholder="0"
                value={props.workplace.coLocatedBusinesses || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  props.onChange('coLocatedBusinesses', Number(e.target.value))
                }
              />
              <Suffix>st</Suffix>
            </InputWrapper>
          </ResidentialWrapper>
        </div>
      )}

      {isOther && (
        <CompanyNameWrapper>
          <TextAreaInput
            label={copy?.workplace.otherBusinessLabel ?? ''}
            placeholder={copy?.workplace.otherBusinessPlaceholder ?? ''}
            value={props.workplace.otherBusinessDescription}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              props.onChange('otherBusinessDescription', e.target.value)
            }
          />
        </CompanyNameWrapper>
      )}

      <ActionButton
        disabled={
          (isCompany && !props.workplace.name.trim()) ||
          (isResidential && props.workplace.apartments <= 0) ||
          (isOther && !props.workplace.otherBusinessDescription.trim()) ||
          !props.selectedSegment
        }
        onClick={props.onClick}
      >
        Fortsätt
      </ActionButton>
    </Wrapper>
  );
};

export default WorkplaceSection;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  padding: 2rem;
`;

const CompanyNameWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.content_background};
  border: 1px solid ${theme.colors.border};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-radius: ${theme.dimensions.components.borderRadius};
  padding: 1rem;

  @media ${device.sm} {
    width: ${theme.dimensions.card.width};
  }
`;

const ResidentialWrapper = styled.div`
  width: 100%;
  max-width: 310px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background-color: ${theme.colors.content_background};
  border: 1px solid ${theme.colors.border};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-radius: ${theme.dimensions.components.borderRadius};
  padding: 1rem;

  @media ${device.sm} {
    width: ${theme.dimensions.card.width};
  }
`;

const InputText = styled(TextInput)`
  margin-top: 1rem;
  padding: 0 36px;
  width: 100%;
  text-align: center;
  border: none;
  border-bottom: 1px solid ${theme.colors.border};
  border-radius: 0;
  &:focus {
    border-color: ${theme.colors.selectedBorder};
    box-shadow: none;
  }
  &::placeholder {
    text-align: center;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 280px;
`;

const Suffix = styled.span`
  position: absolute;
  right: 12px;
  top: 62px;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
`;

const SliderWrapper = styled(Slider)`
  width: 100%;
  background-color: ${theme.colors.content_background};
  border: 1px solid ${theme.colors.border};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-radius: ${theme.dimensions.components.borderRadius};
  padding: 1rem;

  @media ${device.sm} {
    max-width: ${theme.dimensions.card.width};
  }
`;

const EmployeesSliderWrapper = styled(SliderWrapper)`
  margin-top: 1rem;
`;

const InputSpacer = styled.div`
  height: 1rem;
`;
